import { useState } from 'react';
import { X, Upload, Download, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
export const BASE_URL = import.meta.env.VITE_API_BASE_URL;

interface BulkUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  uploadType: 'categories' | 'menuItems';
  menuTypeId?: string; // Auto-assign this menu type to all uploaded categories
  onUploadComplete?: () => void; // Callback to refresh data
}

export function BulkUploadModal({ isOpen, onClose, uploadType, menuTypeId, onUploadComplete }: BulkUploadModalProps) {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);

  if (!isOpen) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        setUploadedFile(file);
        setUploadSuccess(false);
      } else {
        toast.error('Please upload a CSV file');
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && (file.type === 'text/csv' || file.name.endsWith('.csv'))) {
      setUploadedFile(file);
      setUploadSuccess(false);
    } else {
      toast.error('Please upload a CSV file');
    }
  };

  const handleUpload = async () => {
    if (!uploadedFile) {
      toast.error('Please select a file to upload');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setErrorMessages([]);

    try {
      // Read the CSV file
      const text = await uploadedFile.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        throw new Error('CSV file is empty or has no data rows');
      }

      // Parse CSV
      const headers = lines[0].split(',').map(h => h.trim());
      const rows = lines.slice(1);
      
      setUploadProgress(10);

      // Get auth token
      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }

      let successCount = 0;
      let failCount = 0;
      const errors: string[] = [];

      // Process each row
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i].trim();
        if (!row) continue;

        try {
          // Parse CSV row (handle quoted values)
          const values = row.match(/(".*?"|[^,]+)(?=\s*,|\s*$)/g)?.map(v => v.replace(/^"|"$/g, '').trim()) || [];
          
          if (values.length === 0) continue;

          const rowData: any = {};
          headers.forEach((header, index) => {
            rowData[header] = values[index] || '';
          });

          if (uploadType === 'categories') {
            // Create category
            if (!menuTypeId) {
              throw new Error('Menu Type ID is required for category upload');
            }

            const response = await fetch(`${BASE_URL}/categories`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({
                name: rowData.name,
                description: rowData.description || '',
                menuTypeId: menuTypeId,
              }),
            });

            if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.message || `Failed to create category: ${rowData.name}`);
            }

            successCount++;
          } else {
            // Create product
            // Step 1: Find or create category to get categoryId
            let categoryId = '';
            const categoryName = rowData.categoryName || 'Uncategorized';
            
            // First, try to find existing category by name
            const categoriesResponse = await fetch(`${BASE_URL}/categories?menuTypeId=${menuTypeId}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
              },
            });
            
            if (categoriesResponse.ok) {
              const categoriesData = await categoriesResponse.json();
              // Handle both array and object responses
              const categories = Array.isArray(categoriesData) ? categoriesData : (categoriesData.data || []);
              const existingCategory = categories.find((cat: any) => cat.name.toLowerCase() === categoryName.toLowerCase());
              
              if (existingCategory) {
                categoryId = existingCategory.id;
              } else {
                // Create new category
                const newCategoryResponse = await fetch(`${BASE_URL}/categories`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                  },
                  body: JSON.stringify({
                    name: categoryName,
                    description: `${categoryName} items`,
                    menuTypeId: menuTypeId,
                  }),
                });
                
                if (newCategoryResponse.ok) {
                  const newCategory = await newCategoryResponse.json();
                  categoryId = newCategory.id;
                } else {
                  throw new Error(`Failed to create category: ${categoryName}`);
                }
              }
            }

            // Step 2: Generate SKU (use product name slug + timestamp)
            const sku = `${rowData.name.toLowerCase().replace(/[^a-z0-9]/g, '-').substring(0, 20)}-${Date.now().toString().slice(-6)}`;

            // Step 3: Create product with correct fields
            const response = await fetch(`${BASE_URL}/products`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({
                name: rowData.name,
                description: rowData.description || '',
                price: parseFloat(rowData.price || '0'),
                categoryId: categoryId,
                sku: sku,
                image: rowData.searchQuery ? `https://source.unsplash.com/400x300/?${encodeURIComponent(rowData.searchQuery)}` : '',
                imageSource: 'url',
                imageMetadata: rowData.searchQuery ? {
                  source: 'unsplash',
                  query: rowData.searchQuery,
                } : undefined,
              }),
            });

            if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.message || `Failed to create product: ${rowData.name}`);
            }

            successCount++;
          }

          // Update progress
          setUploadProgress(10 + Math.floor((i / rows.length) * 90));
        } catch (error: any) {
          failCount++;
          errors.push(`Row ${i + 2}: ${error.message}`);
          console.error(`Error processing row ${i + 2}:`, error);
        }
      }

      setUploadProgress(100);
      setUploading(false);
      setUploadedCount(successCount);

      if (successCount > 0) {
        setUploadSuccess(true);
        toast.success(`Successfully uploaded ${successCount} ${uploadType === 'categories' ? 'categories' : 'products'}!`);
        
        // Call refresh callback
        if (onUploadComplete) {
          onUploadComplete();
        }
      }

      if (failCount > 0) {
        setErrorMessages(errors.slice(0, 10)); // Show first 10 errors
        toast.error(`${failCount} items failed to upload. Check the error messages below.`);
      }

    } catch (error: any) {
      setUploading(false);
      setUploadProgress(0);
      toast.error(`Upload failed: ${error.message}`);
      setErrorMessages([error.message]);
      console.error('Upload error:', error);
    }
  };

  const downloadTemplate = () => {
    let csvContent = '';
    
    if (uploadType === 'categories') {
      // Template for categories (no displayOrder - backend doesn't accept it)
      csvContent = 'name,description\\n' +
                   'Breakfast,Start your day with our delicious breakfast options\\n' +
                   'Lunch,Traditional lunch options\\n' +
                   'Starters & Soups,Appetizers and soups to start your meal\\n' +
                   'Beverages,Hot and cold beverages';
    } else {
      // Template for products - matches your CSV format
      csvContent = 'name,description,price,categoryName,searchQuery\\n' +
                   '2 Fried Eggs,Served with potatoes & toast,250,Breakfast,fried eggs breakfast\\n' +
                   'Chicken Biryani,Aromatic chicken biryani,600,Biryani & Rice,chicken biryani\\n' +
                   'BBQ Chicken Burger,BBQ chicken burger with fries,850,Burgers,bbq chicken burger\\n' +
                   'Masala Tea,Tea infused with spices,350,Tea & Coffee,masala chai tea';
    }

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${uploadType}_template.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Template downloaded successfully!');
  };

  const handleClose = () => {
    setUploadedFile(null);
    setUploading(false);
    setUploadProgress(0);
    setUploadSuccess(false);
    setUploadedCount(0);
    setErrorMessages([]);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Bulk Upload {uploadType === 'categories' ? 'Categories' : 'Menu Items'}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Upload multiple {uploadType === 'categories' ? 'categories' : 'menu items'} at once using a CSV file
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Download Template Section */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900 dark:text-blue-300 mb-1">
                  Step 1: Download Template
                </h3>
                <p className="text-sm text-blue-700 dark:text-blue-400 mb-3">
                  Download our CSV template with the correct format and column headers
                </p>
                <button
                  onClick={downloadTemplate}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  <Download className="w-4 h-4" />
                  Download CSV Template
                </button>
              </div>
            </div>
          </div>

          {/* Upload Section */}
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
              Step 2: Upload Your CSV File
            </h3>
            
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                isDragging
                  ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20'
                  : uploadedFile
                  ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                  : 'border-gray-300 dark:border-gray-700'
              }`}
            >
              {uploadedFile ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-center">
                    <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{uploadedFile.name}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {(uploadedFile.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                  <button
                    onClick={() => setUploadedFile(null)}
                    className="text-sm text-red-600 dark:text-red-400 hover:underline"
                  >
                    Remove file
                  </button>
                </div>
              ) : (
                <>
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-900 dark:text-white font-medium mb-2">
                    Drag and drop your CSV file here
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">or</p>
                  <label
                    htmlFor="file-upload"
                    className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
                  >
                    <Upload className="w-4 h-4" />
                    Browse Files
                  </label>
                  <input
                    id="file-upload"
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </>
              )}
            </div>

            {/* Upload Progress */}
            {uploading && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Uploading...
                  </span>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {uploadProgress}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Success Message */}
            {uploadSuccess && (
              <div className="mt-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-green-900 dark:text-green-300 mb-1">
                      Upload Successful!
                    </h4>
                    <p className="text-sm text-green-700 dark:text-green-400">
                      Successfully uploaded {uploadedCount} {uploadType === 'categories' ? 'categories' : 'menu items'} to your database.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Messages */}
            {errorMessages.length > 0 && (
              <div className="mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-semibold text-red-900 dark:text-red-300 mb-2">
                      Upload Errors
                    </h4>
                    <div className="text-sm text-red-700 dark:text-red-400 space-y-1 max-h-40 overflow-y-auto">
                      {errorMessages.map((error, index) => (
                        <div key={index} className="font-mono text-xs">
                          {error}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Instructions */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-gray-600 dark:text-gray-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                  Important Notes:
                </h4>
                <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1 list-disc list-inside">
                  <li>Make sure your CSV file follows the template format exactly</li>
                  <li>Column headers must match the template (case-sensitive)</li>
                  {uploadType === 'categories' && menuTypeId && (
                    <li className="text-green-600 dark:text-green-400 font-medium">✓ All categories will be automatically linked to the selected menu type</li>
                  )}
                  {uploadType === 'menuItems' && (
                    <li><strong>Replace CATEGORY_ID_HERE</strong> with actual Category IDs from your categories</li>
                  )}
                  <li>Use UTF-8 encoding for special characters</li>
                  <li>Maximum file size: 5MB</li>
                  <li>Existing items with the same name will be skipped</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            {uploadSuccess ? 'Close' : 'Cancel'}
          </button>
          {!uploadSuccess && (
            <button
              onClick={handleUpload}
              disabled={!uploadedFile || uploading}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload className="w-4 h-4" />
              {uploading ? 'Uploading...' : 'Upload File'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}